import { useTRPC } from '@/utils/trpc';
import {
  AddFamilyMemberHistoryForm,
  type AddFamilyMemberHistoryFormValues,
} from '@liza/ui/components/records/AddFamilyMemberHistoryForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useValueSetExpand } from '@liza/ui/hooks/useValueSetExpand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const RELATIONSHIP_VALUE_SET_URL = 'http://terminology.hl7.org/ValueSet/v3-FamilyMember';
const CONDITION_CODE_VALUE_SET_URL = 'http://hl7.org/fhir/us/core/ValueSet/us-core-condition-code';

type Props = {
  medplumPatientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddFamilyMemberHistoryDialog = ({ medplumPatientId, isOpen, onOpenChange }: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const relationship = useValueSetExpand({
    queryOptions: (filter) => trpc.valueSet.expand.queryOptions({ url: RELATIONSHIP_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });
  const conditionCode = useValueSetExpand({
    queryOptions: (filter) =>
      trpc.valueSet.expand.queryOptions({ url: CONDITION_CODE_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });

  const { mutateAsync: createFamilyMemberHistory, isPending: isCreating } = useMutation(
    trpc.patient.familyMemberHistory.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.familyMemberHistory.list.queryKey({ medplumPatientId }),
        });
      },
    }),
  );

  const handleCreate = useCallback(
    async (values: AddFamilyMemberHistoryFormValues): Promise<void> => {
      try {
        await createFamilyMemberHistory({
          resource: {
            resourceType: 'FamilyMemberHistory',
            status: 'completed',
            patient: { reference: `Patient/${medplumPatientId}` },
            relationship: {
              coding: [values.relationship],
              text: values.relationship.display ?? values.relationship.code,
            },
            name: values.name || undefined,
            condition: values.conditionCode
              ? [
                  {
                    code: {
                      coding: [values.conditionCode],
                      text: values.conditionCode.display ?? values.conditionCode.code,
                    },
                  },
                ]
              : undefined,
            note: values.notes ? [{ text: values.notes }] : undefined,
            date: new Date().toISOString(),
          },
        });
        toast.success(t('familyMemberHistoryAdded'));
        onOpenChange(false);
      } catch (error) {
        toast.error(`${t('addFamilyMemberHistoryFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createFamilyMemberHistory, medplumPatientId, onOpenChange, t],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('addFamilyMemberHistory')}</DialogTitle>
        </DialogHeader>
        <AddFamilyMemberHistoryForm
          relationship={relationship}
          conditionCode={conditionCode}
          isCreating={isCreating}
          onClose={() => onOpenChange(false)}
          onCreate={handleCreate}
        />
      </DialogContent>
    </Dialog>
  );
};
